import {
  Box,
  Heading,
  Text,
  Flex,
  InputLeftAddon,
  Input,
  InputGroup,
  Textarea,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { omit } from 'remeda';

import { finleyAssert, path as commonPath } from '@finley-technologies/common';
import { FinleyAi, typeguards } from '@finley-technologies/finley-types';

import { ExtractValuesViewContext, FinleyAiViewContext } from './types';
import {
  DeleteIconButton,
  AddIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { UploadModal } from 'packages/common-frontend/components/ReactFlow/UploadModal';
import { Select } from 'packages/common-frontend/components/Select';
import { useFinleyAiExtractValuesDirectUpload } from 'packages/finleyAi/hooks/extractValuesDirectUpload';

export const extractValuesOptions: {
  value: FinleyAi.ExtractValuesSubType;
  label: string;
}[] = [{ value: 'extractJson' as const, label: 'Extract Json' }];

export const extractValuesDataTypeOptions: {
  value: FinleyAi.FinleyAiExtractedValueQuestionConfigDataType;
  label: string;
}[] = [
  { value: 'string' as const, label: 'string' },
  { value: 'integer' as const, label: 'integer' },
  { value: 'float' as const, label: 'float' },
  { value: 'boolean' as const, label: 'boolean' },
];

const PageNumbersInput = ({
  usePages,
  setViewContext,
  isLocked,
}: {
  usePages?: number[];
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
  isLocked: boolean;
}) => {
  const addPageNumber = (pageNumber: number) => {
    setViewContext((vc) => {
      finleyAssert(
        vc.type === 'extractValues',
        'FinleyAi view context must be extractValues',
      );
      if (!vc.usePages) {
        return { ...vc, usePages: [pageNumber] };
      }

      return { ...vc, usePages: [...vc.usePages, pageNumber] };
    });
  };

  const removePageNumber = (idx: number) => {
    setViewContext((vc) => {
      finleyAssert(
        vc.type === 'extractValues',
        'FinleyAi view context must be extractValues',
      );
      if (!vc.usePages) {
        return vc;
      }

      const pagesToUse = vc.usePages.filter((__, index) => index !== idx);

      if (!pagesToUse.length) {
        return omit(vc, ['usePages']);
      }

      return { ...vc, usePages: pagesToUse };
    });
  };

  const handlePageNumberChange = (idx: number, pageNumber: string) => {
    setViewContext((vc) => {
      finleyAssert(
        vc.type === 'extractValues',
        'FinleyAi view context must be extractValues',
      );
      if (!vc.usePages) {
        return vc;
      }

      const pagesToUse = vc.usePages.map((pageNum, index) => {
        if (index === idx) {
          return Number(pageNumber);
        }
        return pageNum;
      });

      return { ...vc, usePages: pagesToUse };
    });
  };

  return (
    <Box mb="3">
      <Flex mb="3" alignItems="center">
        <Heading as="h5" size="md">
          Page Numbers
        </Heading>
        <AddIconButton
          label="Add Page Number"
          ariaLabel="Add Page Number"
          onClick={() => addPageNumber((usePages?.length ?? 0) + 1)}
          isDisabled={isLocked}
        />
      </Flex>
      {usePages?.map((pageNumber, idx) => (
        <Flex
          alignItems="center"
          mb="2"
          // eslint-disable-next-line react/no-array-index-key
          key={`page-number-${idx}`}
        >
          <Input
            type="number"
            value={pageNumber}
            onChange={(e) => handlePageNumberChange(idx, e.target.value)}
            width="sm"
            mr="10px"
            isDisabled={isLocked}
          />
          <DeleteIconButton
            label="Remove page number"
            ariaLabel="Remove page number"
            onClick={() => removePageNumber(idx)}
            isDisabled={isLocked}
          />
        </Flex>
      ))}
    </Box>
  );
};

const QuestionConfig = ({
  questionConfig,
  setViewContext,
  idx,
  isLocked,
}: {
  questionConfig: FinleyAi.FinleyAiExtractedValueQuestionConfig;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
  idx: number;
  isLocked: boolean;
}) => {
  return (
    <Box mb="3">
      <InputGroup>
        <InputLeftAddon>Field Name</InputLeftAddon>
        <Input
          type="text"
          placeholder="fieldName"
          isDisabled={isLocked}
          value={questionConfig.fieldName}
          onChange={(e) => {
            setViewContext((viewContext) => {
              finleyAssert(
                viewContext.type === 'extractValues',
                'View Context must be extract values.',
              );
              const vc = _.cloneDeep(viewContext);
              vc.questionConfigs[idx].fieldName = e.target.value;
              return vc;
            });
          }}
        />
        <DeleteIconButton
          label="delete"
          ariaLabel="delete"
          isDisabled={isLocked}
          onClick={() => {
            setViewContext((viewContext) => {
              finleyAssert(
                viewContext.type === 'extractValues',
                'View Context must be extract values.',
              );
              const vc = _.cloneDeep(viewContext);
              vc.questionConfigs = vc.questionConfigs.filter(
                (_qc, index) => index !== idx,
              );
              return vc;
            });
          }}
        />
      </InputGroup>
      <InputGroup>
        <InputLeftAddon>Question</InputLeftAddon>
        <Textarea
          placeholder="question"
          value={questionConfig.question}
          isDisabled={isLocked}
          onChange={(e) => {
            setViewContext((viewContext) => {
              finleyAssert(
                viewContext.type === 'extractValues',
                'View Context must be extract values.',
              );
              const vc = _.cloneDeep(viewContext);
              vc.questionConfigs[idx].question = e.target.value;
              return vc;
            });
          }}
        />
      </InputGroup>
      <InputGroup>
        <InputLeftAddon>Extraction Type</InputLeftAddon>
        <Select
          value={questionConfig.type}
          isDisabled={isLocked}
          onChange={(val) => {
            if (val) {
              setViewContext((viewContext) => {
                finleyAssert(
                  viewContext.type === 'extractValues',
                  'View Context must be extract values.',
                );
                const vc = _.cloneDeep(viewContext);
                vc.questionConfigs[idx].type = val;
                return vc;
              });
            }
          }}
          options={extractValuesDataTypeOptions}
          width="sm"
        />
      </InputGroup>
    </Box>
  );
};

const EngineSelection = ({
  viewContext,
  setViewContext,
  isLocked,
}: {
  viewContext: ExtractValuesViewContext;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
  isLocked: boolean;
}) => {
  const { s3Path } = viewContext;

  const extension = useMemo(() => {
    try {
      const ext = commonPath.getFileExtension(s3Path);

      if (
        !typeguards.finleyAi.isFinleyAiExtractValuesSupportedFileExtension(ext)
      ) {
        return null;
      }

      return ext;
    } catch {
      return null;
    }
  }, [s3Path]);

  if (!extension) {
    return null;
  }

  const options: (FinleyAi.FinleyAiExtractValuesEngine | 'default')[] = [
    ...FinleyAi.FinleyAiExtractValuesFileExtToEngineSupport[extension],
    'default',
  ];

  const selectOptions: {
    value: FinleyAi.FinleyAiExtractValuesEngine | 'default';
    label: string;
  }[] = options.map((val) => ({ value: val, label: val }));

  return (
    <Box>
      <Flex alignItems="center" mb="3">
        <Text mr="3px">Engine:</Text>
        <Select
          value={viewContext.extractionEngine}
          onChange={(val) => {
            if (val) {
              setViewContext((vc) => {
                finleyAssert(
                  vc.type === 'extractValues',
                  'View Context must be extract values.',
                );
                return { ...vc, extractionEngine: val };
              });
            }
          }}
          options={selectOptions}
          width="sm"
          isDisabled={isLocked}
        />
      </Flex>
    </Box>
  );
};

type FinleyAiExtractValuesProps = {
  targetOrganizationId: string;
  isLocked: boolean;
  viewContext: ExtractValuesViewContext;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
};

export const FinleyAiExtractValues = ({
  targetOrganizationId,
  isLocked,
  viewContext,
  setViewContext,
}: FinleyAiExtractValuesProps) => {
  const toast = useToast();

  const [documentName, setDocumentName] = useState<string>('');
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { directUpload } = useFinleyAiExtractValuesDirectUpload({
    targetOrganizationId,
  });

  const upload = useCallback(
    async (file: File) => {
      if (documentName !== '') {
        try {
          const fileExtension = commonPath.getFileExtension(file.name);

          finleyAssert(
            typeguards.finleyAi.isFinleyAiExtractValuesSupportedFileExtension(
              fileExtension,
            ),
            `[fileExtension:${fileExtension}] is not a supported file extension.`,
          );
          const { s3Bucket, s3Path } = await directUpload({
            file,
            documentName,
            fileExtension,
          });
          setViewContext((vc) => ({
            ...vc,
            s3Bucket,
            s3Path,
            extractionEngine: 'default', // set engine to default on new upload, in event file ext changes
          }));
          setDocumentName('');
        } catch (e) {
          toast({
            title: 'Document upload failed. Please see technical assistance.',
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Document Name must be specified.',
          status: 'error',
          position: 'top',
          isClosable: true,
        });
      }
    },
    [directUpload, toast, documentName, setViewContext],
  );

  return (
    <Box mr="20px">
      <Heading as="h4" size="md" mb="3">
        Extract Values Configuration
      </Heading>
      <Flex alignItems="center" mb="3">
        <Text mr="3px">Mode:</Text>
        <Select
          value={viewContext.subType}
          onChange={(val) => {
            if (val) {
              setViewContext((vc) => ({ ...vc, subType: val }));
            }
          }}
          options={extractValuesOptions}
          width="sm"
          isDisabled={isLocked}
        />
      </Flex>
      <Flex mb="3" alignItems="center">
        <Text>
          Loaded Document:{' '}
          {_.last(viewContext.s3Path.split('/')) ||
            'Load document prior to continuing.'}
        </Text>
        <UploadIconButton
          ariaLabel="upload"
          label="upload"
          onClick={onOpen}
          isDisabled={isLocked}
        />
        <UploadModal
          modalHeaderText="Upload Document"
          onClose={onClose}
          isOpen={isOpen}
          handleFile={upload}
          allowedExtensions={[
            'pdf',
            'html',
            'png',
            'tiff',
            'tif',
            'jpg',
            'jpeg',
            'txt',
            'json',
          ]}
        >
          <InputGroup mb="3">
            <InputLeftAddon>Document Name</InputLeftAddon>
            <Input
              type="text"
              placeholder="documentName"
              value={documentName}
              onChange={(e) => {
                setDocumentName(e.target.value);
              }}
            />
          </InputGroup>
        </UploadModal>
      </Flex>

      <Flex alignItems="center" mb="3">
        <Heading as="h5" size="md">
          Question Configurations
        </Heading>
        <AddIconButton
          ariaLabel="add"
          label="add"
          isDisabled={isLocked}
          onClick={() =>
            setViewContext((viewCont) => {
              finleyAssert(
                viewCont.type === 'extractValues',
                'View Context must be extract values.',
              );
              const vc = _.cloneDeep(viewCont);
              vc.questionConfigs.push({
                fieldName: '',
                question: '',
                type: 'string',
              });
              return vc;
            })
          }
        />
      </Flex>
      {viewContext.questionConfigs.map((questionConfig, idx) => {
        return (
          <QuestionConfig
            // eslint-disable-next-line react/no-array-index-key
            key={`question-config-${idx}`}
            questionConfig={questionConfig}
            idx={idx}
            setViewContext={setViewContext}
            isLocked={isLocked}
          />
        );
      })}
      <PageNumbersInput
        usePages={viewContext.usePages}
        setViewContext={setViewContext}
        isLocked={isLocked}
      />
      <EngineSelection
        viewContext={viewContext}
        setViewContext={setViewContext}
        isLocked={isLocked}
      />
    </Box>
  );
};
